import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { IncomeData } from '../../useIncome';
import { BusinessFormValues, dataToForm, UpsertIncomeDTO, Validation } from './BusinessForm.meta';
import { IncomeModel, IncomeType } from '../../api/useIncomeQuery';
import { useUpsertIncomeMutation } from '../../api/useUpsertIncomeMutation';
import { toast } from '../../../../CommonComponents/OPL/Toast/Toast';
import { ADD_INCOME_SUCCESS_MESSAGE, EDIT_INCOME_SUCCESS_MESSAGE } from '../../Income.meta';

const FIELDS_TO_SHOW_ADVANCED = ['IncomeGrowth', 'StartAge', 'EndAge', 'SelfEmployed'];

interface Params {
  clientId: string;
  income: IncomeModel | null;
  data: IncomeData;
  onSuccess: () => void;
}

export const useBusinessForm = ({ clientId, income, data, onSuccess }: Params) => {
  const formBag = useForm<BusinessFormValues>({
    defaultValues: dataToForm(income, data.Options),
    resolver: yupResolver(Validation),
  });

  const mutationBag = useUpsertIncomeMutation({ clientId });

  const defaultShowAdvanced =
    !!income && FIELDS_TO_SHOW_ADVANCED.some(field => !!income[field] || income[field] === 0);

  const startYearOptions = [
    { value: null, label: 'Start Today' },
    ...data.Options.StartYearOptions.map(year => ({
      value: year,
      label: year,
    })),
  ];

  const endYearOptions = [
    { value: null, label: 'At Retirement' },
    ...data.Options.EndYearOptions.map(year => ({
      value: year,
      label: year,
    })),
  ];

  const endAtRetirement = formBag.watch('endAtRetirement');

  const onSubmit = formBag.handleSubmit(async (values: BusinessFormValues) => {
    const body: UpsertIncomeDTO = {
      clientId,
      ...values,
      startAge: values.startYear || null,
      endAge: values.endAtRetirement ? null : values.endYear || null,
      id: income?.Id || null,
      incomeType: IncomeType.Business,
    };

    try {
      await mutationBag.mutateAsync(body);

      toast.success(income?.Id ? EDIT_INCOME_SUCCESS_MESSAGE : ADD_INCOME_SUCCESS_MESSAGE);

      onSuccess();
    } catch (error) {
      console.error(error);
    }
  });

  return {
    data: {
      formBag,
      defaultShowAdvanced,
      startYearOptions,
      endYearOptions,
      endAtRetirement,
      mutationBag,
    },
    actions: {
      onSubmit,
    },
  };
};
